// useMemoriesData.js
import { useEffect, useState } from "react";
import axios from "axios";
import { voxToast } from "@/lib/voxToast";

const LS_KEY = "vox.memories";
const OFFLINE_KEY = "vox.memories.offline";
const EVT = "vox:memories-updated";

const API_STYLE = process.env.REACT_APP_API_STYLE || "backend"; // "backend" | "postgrest"
const DATA_API_BASE = process.env.REACT_APP_DATA_API_BASE || "";
const DATA_API_KEY = process.env.REACT_APP_DATA_API_KEY || "";

function neonHeaders() {
  const h = { "Content-Type": "application/json" };
  if (DATA_API_KEY) {
    h["apikey"] = DATA_API_KEY;
    h["Authorization"] = `Bearer ${DATA_API_KEY}`;
  }
  return h;
}

async function apiList() {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.get(
      `${DATA_API_BASE}/memories?order=created_at.desc`,
      { headers: neonHeaders() }
    );
  }
  return axios.get("/api/memories");
}

async function apiCreate(payload) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.post(
      `${DATA_API_BASE}/memories`,
      payload,
      { headers: neonHeaders() }
    );
  }
  return axios.post("/api/memories", payload);
}

async function apiPatch(id, patch) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.patch(
      `${DATA_API_BASE}/memories?id=eq.${id}`,
      patch,
      { headers: neonHeaders() }
    );
  }
  return axios.patch(`/api/memories/${id}`, patch);
}

async function apiDelete(id) {
  if (API_STYLE === "postgrest" && DATA_API_BASE) {
    return axios.delete(
      `${DATA_API_BASE}/memories?id=eq.${id}`,
      { headers: neonHeaders() }
    );
  }
  return axios.delete(`/api/memories/${id}`);
}

export function useMemoriesData() {
  const [memories, setMemories] = useState([]);

  // ----------------------
  // Load
  // ----------------------
  async function load() {
    try {
      const offline = localStorage.getItem(OFFLINE_KEY) === "1";
      if (offline) {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) {
          setMemories(JSON.parse(cached));
          return;
        }
      }

      const res = await apiList();
      let list = Array.isArray(res.data) ? res.data : [];

      try {
        const cached = localStorage.getItem(LS_KEY);
        if ((!list || list.length === 0) && cached) {
          list = JSON.parse(cached);
        }
      } catch {}

      setMemories(list);
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(list));
        localStorage.setItem(OFFLINE_KEY, "0");
      } catch {}
    } catch {
      try {
        const cached = localStorage.getItem(LS_KEY);
        if (cached) setMemories(JSON.parse(cached));
        localStorage.setItem(OFFLINE_KEY, "1");
        voxToast.offline();
      } catch {}
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener(EVT, handler);
    return () => window.removeEventListener(EVT, handler);
  }, []);

  // ----------------------
  // Mutations
  // ----------------------
  async function add(content, category) {
    try {
      const res = await apiCreate({ content, category });
      const created =
        Array.isArray(res.data) ? res.data[0] : res.data;

      setMemories((prev) => {
        const list = [...prev, created];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "0");
        } catch {}
        return list;
      });

      voxToast.memoryAdded();
      window.dispatchEvent(new CustomEvent(EVT));
    } catch {
      voxToast.memoryFailed();

      const item = {
        id: crypto.randomUUID(),
        content,
        category,
      };

      setMemories((prev) => {
        const list = [...prev, item];
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(list));
          localStorage.setItem(OFFLINE_KEY, "1");
        } catch {}
        return list;
      });

      window.dispatchEvent(new CustomEvent(EVT));
    }
  }

  async function edit(id, patch) {
    try {
      await apiPatch(id, patch);
      setMemories((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...patch } : m))
      );
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify(
            memories.map((m) =>
              m.id === id ? { ...m, ...patch } : m
            )
          )
        );
      } catch {}
    } catch {
      setMemories((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...patch } : m))
      );
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
        voxToast.offline();
      } catch {}
    }
    window.dispatchEvent(new CustomEvent(EVT));
  }

  async function remove(id) {
    try {
      await apiDelete(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch {
      setMemories((prev) => prev.filter((m) => m.id !== id));
      try {
        localStorage.setItem(OFFLINE_KEY, "1");
        voxToast.offline();
      } catch {}
    }
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify(memories.filter((m) => m.id !== id))
      );
    } catch {}
    window.dispatchEvent(new CustomEvent(EVT));
  }

  return { memories, load, add, edit, remove };
}
